import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Popover,
  useMediaQuery,
  useTheme,
  Collapse,
  Divider,
  Grid,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { toast, ToastContainer } from "react-toastify";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import "react-toastify/dist/ReactToastify.css";
import {
  DivisionCreate,
  DivisionList,
  DivisionDelete,
  DivisionUpdate,
} from "../../../Services/Services";

const DivisionManagementTable = () => {
  const [divisions, setDivisions] = useState([]);
  const [filter, setFilter] = useState("");
  const [sortAsc] = useState(true);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [divisionToDelete, setDivisionToDelete] = useState(null);
  const [errors, setErrors] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedDivision, setExpandedDivision] = useState(null);

  // Form state for Add Modal
  const [newDivisionName, setNewDivisionName] = useState("");
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newGstNo, setNewGstNo] = useState("");
  const [newPanNo, setNewPanNo] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [newLatitude, setNewLatitude] = useState("");
  const [newLongitude, setNewLongitude] = useState("");
  const [newRadius, setNewRadius] = useState(200);

  // Form state for Edit Modal
  const [editDivisionName, setEditDivisionName] = useState("");
  const [editCompanyName, setEditCompanyName] = useState("");
  const [editGstNo, setEditGstNo] = useState("");
  const [editPanNo, setEditPanNo] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editLatitude, setEditLatitude] = useState("");
  const [editLongitude, setEditLongitude] = useState("");
  const [editRadius, setEditRadius] = useState(200);

  useEffect(() => {
    refreshDivisions();
  }, []);

  function formatOnlyDate(dateTimeString) {
    if (!dateTimeString) return "N/A";
    const datePart = dateTimeString.split("T")[0];
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year}`;
  }

  const refreshDivisions = async () => {
    try {
      const res = await DivisionList();
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.results)
        ? res.results
        : [];
      setDivisions(list);
    } catch (error) {
      console.error("Error fetching divisions:", error);
      toast.error("Unable to load divisions");
    }
  };

  const resetAddForm = () => {
    setNewDivisionName("");
    setNewCompanyName("");
    setNewGstNo("");
    setNewPanNo("");
    setNewAddress("");
    setNewLatitude("");
    setNewLongitude("");
    setNewRadius(200);
    setErrors({});
  };

  const resetEditForm = () => {
    setEditDivisionName("");
    setEditCompanyName("");
    setEditGstNo("");
    setEditPanNo("");
    setEditAddress("");
    setEditLatitude("");
    setEditLongitude("");
    setEditRadius(200);
    setEditingIndex(null);
    setErrors({});
  };

  const validateGstNo = (value) => {
    if (!value || value.trim() === "") return true;
    const gstPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstPattern.test(value.toUpperCase());
  };

  const validatePanNo = (value) => {
    if (!value || value.trim() === "") return true;
    const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return panPattern.test(value.toUpperCase());
  };

  const validateLatLng = (lat, lng) => {
    const errs = {};
    if (lat !== "" && lat !== null && lat !== undefined) {
      const n = parseFloat(lat);
      if (isNaN(n) || n < -90 || n > 90) errs.latitude = "Latitude must be -90 to 90";
    }
    if (lng !== "" && lng !== null && lng !== undefined) {
      const n = parseFloat(lng);
      if (isNaN(n) || n < -180 || n > 180) errs.longitude = "Longitude must be -180 to 180";
    }
    return errs;
  };

  const validateRadius = (r) => {
    if (r === "" || r === null || r === undefined) return null;
    const n = parseInt(r);
    if (isNaN(n) || n <= 0 || n > 100000) return "Radius must be 1-100000 meters";
    return null;
  };

  const fetchCurrentLocation = (setLat, setLng) => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }
    toast.info("Fetching current location...");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude.toFixed(6));
        setLng(pos.coords.longitude.toFixed(6));
        toast.success("Location fetched");
      },
      () => toast.error("Location permission denied"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleEdit = (division) => {
    const editRow = (Array.isArray(divisions) ? divisions : []).find(
      (d) => d.division_id === division.division_id
    );
    if (editRow) {
      setErrors({});
      setEditDivisionName(editRow.division || "");
      setEditCompanyName(editRow.company_name || "");
      setEditGstNo(editRow.gst_no || "");
      setEditPanNo(editRow.pan_no || "");
      setEditAddress(editRow.address || "");
      setEditLatitude(editRow.latitude ?? "");
      setEditLongitude(editRow.longitude ?? "");
      setEditRadius(editRow.allowed_radius ?? 200);
      setEditingIndex(editRow);
      setOpenEditModal(true);
    }
  };

  const handleUpdateDivision = async () => {
    const trimmedName = editDivisionName.trim();
    const trimmedCompany = editCompanyName.trim();
    const trimmedGst = editGstNo.trim().toUpperCase();
    const trimmedPan = editPanNo.trim().toUpperCase();
    const trimmedAddress = editAddress.trim();

    const newErrors = {};
    if (!trimmedName) newErrors.division = "Division name is required.";
    if (!trimmedCompany) newErrors.company_name = "Company name is required.";
    if (trimmedGst && !validateGstNo(trimmedGst))
      newErrors.gst_no = "Invalid GST format. Example: 22AAAAA0000A1Z5";
    if (trimmedPan && !validatePanNo(trimmedPan))
      newErrors.pan_no = "Invalid PAN format. Example: ABCDE1234F";
    if (trimmedAddress && trimmedAddress.length < 5)
      newErrors.address = "Address must be at least 5 characters.";

    Object.assign(newErrors, validateLatLng(editLatitude, editLongitude));
    const rerr = validateRadius(editRadius);
    if (rerr) newErrors.allowed_radius = rerr;

    if (trimmedGst && trimmedPan) {
      const panFromGst = trimmedGst.substring(2, 12);
      if (panFromGst !== trimmedPan)
        newErrors.pan_no = "PAN Number does not match with GST Number.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const exists = (Array.isArray(divisions) ? divisions : []).some(
      (d) =>
        d.division_id !== editingIndex.division_id &&
        d.division?.trim().toLowerCase() === trimmedName.toLowerCase() &&
        d.company_name?.trim().toLowerCase() === trimmedCompany.toLowerCase()
    );
    if (exists) {
      toast.info("Division with this company name already exists");
      setErrors({ division: "Already exists" });
      return;
    }

    setErrors({});
    try {
      await DivisionUpdate(editingIndex.division_id, {
        division: trimmedName,
        company_name: trimmedCompany,
        gst_no: trimmedGst || null,
        pan_no: trimmedPan || null,
        address: trimmedAddress || null,
        latitude: editLatitude !== "" ? parseFloat(editLatitude) : null,
        longitude: editLongitude !== "" ? parseFloat(editLongitude) : null,
        allowed_radius: editRadius !== "" ? parseInt(editRadius) : 200,
      });
      toast.success("Division updated successfully!");
      setOpenEditModal(false);
      resetEditForm();
      await refreshDivisions();
    } catch (error) {
      const serverErrors = error?.response?.data;
      if (serverErrors && typeof serverErrors === "object") setErrors(serverErrors);
      toast.error("Failed to update division");
    }
  };

  const getFilteredSortedDivisions = () => {
    const list = Array.isArray(divisions) ? divisions : [];
    let filtered = list.filter(
      (d) =>
        d?.division?.toLowerCase().includes(filter.toLowerCase()) ||
        d?.company_name?.toLowerCase().includes(filter.toLowerCase()) ||
        d?.gst_no?.toLowerCase().includes(filter.toLowerCase()) ||
        d?.pan_no?.toLowerCase().includes(filter.toLowerCase()) ||
        d?.address?.toLowerCase().includes(filter.toLowerCase())
    );
    filtered.sort((a, b) =>
      sortAsc
        ? a.division.localeCompare(b.division)
        : b.division.localeCompare(a.division)
    );
    return filtered;
  };

  const handleAddNewDivision = async () => {
    const trimmedDivision = newDivisionName.trim();
    const trimmedCompany = newCompanyName.trim();
    const trimmedGst = newGstNo.trim().toUpperCase();
    const trimmedPan = newPanNo.trim().toUpperCase();
    const trimmedAddress = newAddress.trim();

    const newErrors = {};
    if (!trimmedDivision) newErrors.division = "Division name is required";
    if (!trimmedCompany) newErrors.company_name = "Company name is required";
    if (trimmedGst && !validateGstNo(trimmedGst))
      newErrors.gst_no = "Invalid GST format. Example: 22AAAAA0000A1Z5";
    if (trimmedPan && !validatePanNo(trimmedPan))
      newErrors.pan_no = "Invalid PAN format. Example: ABCDE1234F";
    if (trimmedAddress && trimmedAddress.length < 5)
      newErrors.address = "Address must be at least 5 characters.";

    Object.assign(newErrors, validateLatLng(newLatitude, newLongitude));
    const rerr = validateRadius(newRadius);
    if (rerr) newErrors.allowed_radius = rerr;

    if (trimmedGst && trimmedPan) {
      const panFromGst = trimmedGst.substring(2, 12);
      if (panFromGst !== trimmedPan)
        newErrors.pan_no = "PAN Number does not match with GST Number.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const exists = (Array.isArray(divisions) ? divisions : []).some(
      (d) =>
        d.division?.trim().toLowerCase() === trimmedDivision.toLowerCase() &&
        d.company_name?.trim().toLowerCase() === trimmedCompany.toLowerCase()
    );
    if (exists) {
      toast.info("Division with this company name already exists");
      setErrors({ division: "Already exists" });
      return;
    }

    setErrors({});
    try {
      await DivisionCreate({
        division: trimmedDivision,
        company_name: trimmedCompany,
        gst_no: trimmedGst || null,
        pan_no: trimmedPan || null,
        address: trimmedAddress || null,
        latitude: newLatitude !== "" ? parseFloat(newLatitude) : null,
        longitude: newLongitude !== "" ? parseFloat(newLongitude) : null,
        allowed_radius: newRadius !== "" ? parseInt(newRadius) : 200,
      });
      toast.success("Division added successfully!");
      resetAddForm();
      setOpenAddModal(false);
      await refreshDivisions();
    } catch (error) {
      const serverErrors = error?.response?.data;
      if (serverErrors && typeof serverErrors === "object") setErrors(serverErrors);
      toast.error("Failed to add division");
    }
  };

  const handleDelete = async (division_id) => {
    try {
      await DivisionDelete(division_id);
      toast.info("Division deleted.");
      await refreshDivisions();
    } catch (error) {
      toast.error("Failed to delete division");
    }
  };

  // ✅ Sticky columns config
  const COL_SNO_LEFT = 0;
  const COL_DIV_LEFT = 55;      // sno width
  const COL_COMP_LEFT = 55 + 130; // division width

  const stickyLeft = (leftPx, isHead = false, bg = "#fff") => ({
    position: "sticky",
    left: leftPx,
    zIndex: isHead ? 5 : 3,
    backgroundColor: isHead ? "#f5f5f5" : bg,
  });

  const stickyRight = (isHead = false, bg = "#fff") => ({
    position: "sticky",
    right: 0,
    zIndex: isHead ? 5 : 3,
    backgroundColor: isHead ? "#f5f5f5" : bg,
    boxShadow: "-2px 0 5px -2px rgba(0,0,0,0.2)",
  });

  // ✅ Mobile Card
  const MobileDivisionCard = ({ division, index }) => {
    const isExpanded = expandedDivision === division.division_id;
    return (
      <Box key={division.division_id}>
        <Box
          onClick={() =>
            setExpandedDivision(
              expandedDivision === division.division_id ? null : division.division_id
            )
          }
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            cursor: "pointer",
            bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
            "&:active": { bgcolor: "#e3f2fd" },
            borderLeft: "4px solid #1976d2",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}>
                #{index + 1}
              </Typography>
              <Typography sx={{ fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}>
                {division.division}
              </Typography>
            </Box>

            {division.company_name && (
              <Typography sx={{ fontSize: "0.7rem", color: "#666", mb: 0.3 }}>
                Company: {division.company_name}
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 2, mb: 0.3 }}>
              {division.gst_no && (
                <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                  GST: {division.gst_no}
                </Typography>
              )}
              {division.pan_no && (
                <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                  PAN: {division.pan_no}
                </Typography>
              )}
            </Box>

            <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
              📍 {division.latitude ?? "-"}, {division.longitude ?? "-"} ·{" "}
              {division.allowed_radius ?? "-"}m
            </Typography>

            <Typography sx={{ fontSize: "0.7rem", color: "#666" }}>
              {formatOnlyDate(division.created_at)}
            </Typography>
          </Box>

          <IconButton size="small" sx={{ ml: 1 }}>
            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
              borderLeft: "4px solid #1976d2",
            }}
          >
            <Typography sx={{ fontSize: "0.72rem", mb: 0.5 }}>
              Division: {division.division}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", mb: 0.5 }}>
              Company: {division.company_name || "N/A"}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", mb: 0.5 }}>
              GST: {division.gst_no || "N/A"}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", mb: 0.5 }}>
              PAN: {division.pan_no || "N/A"}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", mb: 0.5 }}>
              Address: {division.address || "N/A"}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", mb: 0.5 }}>
              Latitude: {division.latitude ?? "N/A"}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", mb: 0.5 }}>
              Longitude: {division.longitude ?? "N/A"}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", mb: 0.5 }}>
              Radius: {division.allowed_radius ?? "N/A"} m
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", mb: 0.5 }}>
              Created: {formatOnlyDate(division.created_at)}
            </Typography>

            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: "flex", gap: 0.6, justifyContent: "flex-end" }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(division);
                }}
                sx={{ bgcolor: "#e8f5e9", width: 32, height: 32 }}
              >
                <EditIcon sx={{ fontSize: "1rem", color: "#4caf50" }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                  setDivisionToDelete(division.division_id);
                }}
                sx={{ bgcolor: "#ffebee", width: 32, height: 32 }}
              >
                <DeleteIcon sx={{ fontSize: "1rem", color: "#f44336" }} />
              </IconButton>
            </Box>
          </Box>
        </Collapse>
        <Divider />
      </Box>
    );
  };

  // 🔽 Reusable location fields (used in Add + Edit modal)
  const renderLocationFields = (lat, setLat, lng, setLng, radius, setRadius) => (
    <>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Latitude"
          value={lat}
          onChange={(e) => {
            setLat(e.target.value);
            setErrors((prev) => ({ ...prev, latitude: undefined }));
          }}
          fullWidth
          size="small"
          error={Boolean(errors.latitude)}
          helperText={errors.latitude || "e.g. 21.2515"}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Longitude"
          value={lng}
          onChange={(e) => {
            setLng(e.target.value);
            setErrors((prev) => ({ ...prev, longitude: undefined }));
          }}
          fullWidth
          size="small"
          error={Boolean(errors.longitude)}
          helperText={errors.longitude || "e.g. 81.6106"}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Radius (m)"
          type="number"
          value={radius}
          onChange={(e) => {
            setRadius(e.target.value);
            setErrors((prev) => ({ ...prev, allowed_radius: undefined }));
          }}
          fullWidth
          size="small"
          error={Boolean(errors.allowed_radius)}
          helperText={errors.allowed_radius || "e.g. 200"}
        />
      </Grid>
      <Grid item xs={12}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<MyLocationIcon />}
          onClick={() => fetchCurrentLocation(setLat, setLng)}
        >
          Use My Current Location
        </Button>
      </Grid>
    </>
  );

  return (
    <Box sx={{ mt: 0 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: -3,
          pt: -2,
          mb: 1,
        }}
      >
        <TextField
          label="Search by division, company, GST, PAN, Address"
          variant="outlined"
          size="small"
          fullWidth
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{
            mr: 2,
            "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
            "& .MuiInputLabel-root": { fontSize: "0.7rem" },
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            resetAddForm();
            setOpenAddModal(true);
          }}
          sx={{ height: 30, borderRadius: 0, fontSize: "0.65rem" }}
        >
          <AddIcon fontSize="small" sx={{ mr: 1 }} /> Division
        </Button>
      </Box>

      {/* Conditional Rendering */}
      <Box id="project-table-print">
        {isMobile ? (
          <Box
            sx={{
              bgcolor: "background.paper",
              pb: 2,
              maxHeight: "70vh",
              overflow: "auto",
            }}
          >
            {getFilteredSortedDivisions().map((division, index) => (
              <MobileDivisionCard
                key={division.division_id}
                division={division}
                index={index}
              />
            ))}
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: 500,
              overflowX: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: 1500,
                tableLayout: "fixed",
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  borderBottom: "1px solid #ccc",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold",
                      color: "#333",
                      borderBottom: "1px solid #999",
                      fontSize: "0.8rem",
                    },
                  }}
                >
                  <TableCell
                    sx={{ ...stickyLeft(COL_SNO_LEFT, true), width: 55, minWidth: 55 }}
                  >
                    S. No.
                  </TableCell>
                  <TableCell
                    sx={{ ...stickyLeft(COL_DIV_LEFT, true), width: 130, minWidth: 130 }}
                  >
                    Division
                  </TableCell>
                  <TableCell
                    sx={{ ...stickyLeft(COL_COMP_LEFT, true), width: 170, minWidth: 170 }}
                  >
                    Company Name
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>GST No.</TableCell>
                  <TableCell sx={{ minWidth: 130 }}>PAN No.</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Address</TableCell>
                  <TableCell sx={{ minWidth: 110 }}>Latitude</TableCell>
                  <TableCell sx={{ minWidth: 110 }}>Longitude</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Radius (m)</TableCell>
                  <TableCell sx={{ minWidth: 110 }}>Date</TableCell>
                  <TableCell
                    className="no-print"
                    sx={{ ...stickyRight(true), width: 110, minWidth: 110 }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredSortedDivisions().map((division, index) => {
                  const rowBg = index % 2 === 0 ? "#fafafa" : "#ffffff";
                  return (
                    <TableRow
                      hover
                      key={division.division_id}
                      sx={{
                        backgroundColor: rowBg,
                        "& td": {
                          padding: "4px 8px",
                          fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                          maxWidth: 200,
                        },
                        "&:hover td": { backgroundColor: "#e3f2fd" },
                      }}
                    >
                      <TableCell sx={{ ...stickyLeft(COL_SNO_LEFT, false, rowBg) }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ ...stickyLeft(COL_DIV_LEFT, false, rowBg) }}>
                        {division.division}
                      </TableCell>
                      <TableCell sx={{ ...stickyLeft(COL_COMP_LEFT, false, rowBg) }}>
                        {division.company_name || "-"}
                      </TableCell>
                      <TableCell>{division.gst_no || "-"}</TableCell>
                      <TableCell>{division.pan_no || "-"}</TableCell>
                      <TableCell title={division.address || ""}>
                        {division.address || "-"}
                      </TableCell>
                      <TableCell>{division.latitude ?? "-"}</TableCell>
                      <TableCell>{division.longitude ?? "-"}</TableCell>
                      <TableCell>{division.allowed_radius ?? "-"}</TableCell>
                      <TableCell>{formatOnlyDate(division.created_at)}</TableCell>
                      <TableCell sx={{ ...stickyRight(false, rowBg) }}>
                        <IconButton
                          sx={{ p: 0, px: 1 }}
                          onClick={() => handleEdit(division)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon transform="scale(0.6)" />
                        </IconButton>
                        <IconButton
                          sx={{ p: 0, px: 1 }}
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setDivisionToDelete(division.division_id);
                          }}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon transform="scale(0.6)" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Edit Modal */}
      <Dialog
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          resetEditForm();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Division</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Division Name"
                value={editDivisionName}
                onChange={(e) => {
                  setEditDivisionName(e.target.value);
                  if (e.target.value.trim() !== "")
                    setErrors((prev) => ({ ...prev, division: undefined }));
                }}
                fullWidth
                required
                error={Boolean(errors.division)}
                helperText={errors.division}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Company Name"
                value={editCompanyName}
                onChange={(e) => {
                  setEditCompanyName(e.target.value);
                  if (e.target.value.trim() !== "")
                    setErrors((prev) => ({ ...prev, company_name: undefined }));
                }}
                fullWidth
                required
                error={Boolean(errors.company_name)}
                helperText={errors.company_name}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="GST Number"
                value={editGstNo}
                onChange={(e) => {
                  setEditGstNo(e.target.value.toUpperCase());
                  setErrors((prev) => ({ ...prev, gst_no: undefined }));
                }}
                fullWidth
                error={Boolean(errors.gst_no)}
                helperText={errors.gst_no || "Format: 22AAAAA0000A1Z5"}
                size="small"
                inputProps={{ maxLength: 15 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="PAN Number"
                value={editPanNo}
                onChange={(e) => {
                  setEditPanNo(e.target.value.toUpperCase());
                  setErrors((prev) => ({ ...prev, pan_no: undefined }));
                }}
                fullWidth
                error={Boolean(errors.pan_no)}
                helperText={errors.pan_no || "Format: ABCDE1234F"}
                size="small"
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                value={editAddress}
                onChange={(e) => {
                  setEditAddress(e.target.value);
                  setErrors((prev) => ({ ...prev, address: undefined }));
                }}
                fullWidth
                multiline
                rows={2}
                error={Boolean(errors.address)}
                helperText={errors.address}
                size="small"
              />
            </Grid>

            {renderLocationFields(
              editLatitude,
              setEditLatitude,
              editLongitude,
              setEditLongitude,
              editRadius,
              setEditRadius
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditModal(false);
              resetEditForm();
            }}
            color="error"
          >
            Close
          </Button>
          <Button onClick={handleUpdateDivision} color="primary" variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Modal */}
      <Dialog
        open={openAddModal}
        onClose={() => {
          setOpenAddModal(false);
          resetAddForm();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Division</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Division Name"
                value={newDivisionName}
                onChange={(e) => {
                  setNewDivisionName(e.target.value);
                  if (e.target.value.trim() !== "")
                    setErrors((prev) => ({ ...prev, division: undefined }));
                }}
                fullWidth
                required
                error={Boolean(errors.division)}
                helperText={errors.division}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Company Name"
                value={newCompanyName}
                onChange={(e) => {
                  setNewCompanyName(e.target.value);
                  if (e.target.value.trim() !== "")
                    setErrors((prev) => ({ ...prev, company_name: undefined }));
                }}
                fullWidth
                required
                error={Boolean(errors.company_name)}
                helperText={errors.company_name}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="GST Number"
                value={newGstNo}
                onChange={(e) => {
                  setNewGstNo(e.target.value.toUpperCase());
                  setErrors((prev) => ({ ...prev, gst_no: undefined }));
                }}
                fullWidth
                error={Boolean(errors.gst_no)}
                helperText={errors.gst_no || "Format: 22AAAAA0000A1Z5"}
                size="small"
                inputProps={{ maxLength: 15 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="PAN Number"
                value={newPanNo}
                onChange={(e) => {
                  setNewPanNo(e.target.value.toUpperCase());
                  setErrors((prev) => ({ ...prev, pan_no: undefined }));
                }}
                fullWidth
                error={Boolean(errors.pan_no)}
                helperText={errors.pan_no || "Format: ABCDE1234F"}
                size="small"
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                value={newAddress}
                onChange={(e) => {
                  setNewAddress(e.target.value);
                  setErrors((prev) => ({ ...prev, address: undefined }));
                }}
                fullWidth
                multiline
                rows={2}
                error={Boolean(errors.address)}
                helperText={errors.address}
                size="small"
              />
            </Grid>

            {renderLocationFields(
              newLatitude,
              setNewLatitude,
              newLongitude,
              setNewLongitude,
              newRadius,
              setNewRadius
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenAddModal(false);
              resetAddForm();
            }}
            color="error"
          >
            Close
          </Button>
          <Button onClick={handleAddNewDivision} color="primary" variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setDivisionToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">Are you sure?</Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button
              color="error"
              size="small"
              onClick={() => {
                handleDelete(divisionToDelete);
                setAnchorEl(null);
                setDivisionToDelete(null);
              }}
            >
              Yes
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                setDivisionToDelete(null);
              }}
            >
              No
            </Button>
          </Box>
        </Box>
      </Popover>

      <ToastContainer position="top-right" autoClose={3000} />
    </Box>
  );
};

export default DivisionManagementTable;